/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Dashboard v1.0.2 - Sección de Ubicaciones en Tiempo Real
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file LocationsSection.tsx
 * @description Gestión de ubicaciones en tiempo real y links de Waze
 * @version 1.0.2
 * @date 2024-11-03
 * @architecture Frontend → Proxy → WASP
 * 
 * FUNCIONALIDADES:
 * ├─ Ver links de Waze activos
 * ├─ Mapa con ubicaciones en tiempo real
 * ├─ Historial de ubicación por contacto
 * └─ Detener tracking de ubicación
 * 
 * ENDPOINTS USADOS:
 * - GET  /api/whatsapp/wasp/waze-links
 * - GET  /api/whatsapp/wasp/locations/active
 * - GET  /api/whatsapp/wasp/locations/history/:contactId
 * - POST /api/whatsapp/wasp/locations/stop-tracking
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, ListGroup, Badge, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt,
  faRoute,
  faClock,
  faStop,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getHatateToken } from '../../helpers/utils';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface WazeLink {
  id: string;
  contactId: string;
  contactName: string;
  url: string;
  createdAt: string;
  expiresAt?: string;
  isActive: boolean;
}

interface ActiveLocation {
  contactId: string;
  contactName: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: string;
  speed?: number;
}

interface LocationsSectionProps {
  user: any;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE
// ═══════════════════════════════════════════════════════════════════════════

const LocationsSection: React.FC<LocationsSectionProps> = ({ user }) => {
  
  // Estados
  const [wazeLinks, setWazeLinks] = useState<WazeLink[]>([]);
  const [activeLocations, setActiveLocations] = useState<ActiveLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stoppingTracking, setStoppingTracking] = useState<string | null>(null);
  
  const apiUrl = "https://backend.anubisai.net/api/";

  // ═══════════════════════════════════════════════════════════════════════
  // HELPER: Request Config
  // ═══════════════════════════════════════════════════════════════════════

  const getRequestConfig = () => ({
    headers: { Authorization: getHatateToken() },
    mlStuff: {
      whatsAppServerPort: user.mlStuff?.whatsAppServerPort,
      whatsAppApiPort: user.mlStuff?.whatsAppApiPort,
      mlServerPort: user.mlStuff?.mlServerPort
    }
  });

  // ═══════════════════════════════════════════════════════════════════════
  // FETCH WAZE LINKS
  // ═══════════════════════════════════════════════════════════════════════

  const fetchWazeLinks = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}whatsapp/wasp/waze-links`,
        getRequestConfig()
      );

      if (response.data.success) {
        setWazeLinks(response.data.links || []);
      }
    } catch (err: any) {
      console.error('Error fetching Waze links:', err);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // FETCH ACTIVE LOCATIONS
  // ═══════════════════════════════════════════════════════════════════════

const fetchActiveLocations = async () => {
  setLoading(true);
  setError(null);
  try {
    const response = await axios.get(
      `${apiUrl}whatsapp/wasp/locations/active`,
      getRequestConfig()
    );

    if (response.data.success) {
      // Mapear datos del backend al formato esperado
      const mappedLocations = (response.data.activeLocations || []).map((loc: any) => ({
        contactId: loc.sender,
        contactName: loc.sender.replace('@s.whatsapp.net', ''), // o buscar nombre real
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy,
        timestamp: loc.timestamp,
        speed: loc.speedKmh
      }));
      
      setActiveLocations(mappedLocations);
    }
  } catch (err: any) {
    setError(err.response?.data?.message || 'Error al cargar ubicaciones');
  } finally {
    setLoading(false);
  }
};

  // ═══════════════════════════════════════════════════════════════════════
  // STOP TRACKING
  // ═══════════════════════════════════════════════════════════════════════

  const handleStopTracking = async (contactId: string, contactName: string) => {
    if (!window.confirm(`¿Detener tracking de "${contactName}"?`)) return;

    setStoppingTracking(contactId);
    try {
      const response = await axios.post(
        `${apiUrl}whatsapp/wasp/locations/stop-tracking`,
        { contactId },
        getRequestConfig()
      );

      if (response.data.success) {
        setSuccess('Tracking detenido');
        fetchActiveLocations();
        fetchWazeLinks();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al detener tracking');
    } finally {
      setStoppingTracking(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

    if (seconds < 60) return `Hace ${seconds}s`;
    if (seconds < 3600) return `Hace ${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `Hace ${Math.floor(seconds / 3600)}h`;
    return `Hace ${Math.floor(seconds / 86400)}d`;
  };

  const getGoogleMapsUrl = (lat: number, lng: number) => {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  // ═══════════════════════════════════════════════════════════════════════
  // EFECTOS
  // ═══════════════════════════════════════════════════════════════════════

  useEffect(() => {
    fetchWazeLinks();
    fetchActiveLocations();

    // Actualizar cada 30 segundos
    const interval = setInterval(() => {
      fetchActiveLocations();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <Card style={{ 
      border: 'none', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Header style={{ 
        backgroundColor: 'var(--bs-body-bg)', 
        borderBottom: '1px solid var(--bs-border-color)', 
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#ef4444' }} />
          <h5 className="mb-0" style={{ fontWeight: 600 }}>Ubicaciones en Tiempo Real</h5>
        </div>
        <Button variant="link" size="sm" onClick={fetchActiveLocations} disabled={loading}>
          {loading ? <Spinner size="sm" /> : 'Actualizar'}
        </Button>
      </Card.Header>

      <Card.Body style={{ padding: '24px' }}>
        
        {/* Alertas */}
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        <Row>
          {/* Columna 1: Links de Waze */}
          <Col md={6}>
            <h6 style={{ fontWeight: 600, marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faRoute} className="me-2" />
              Links de Waze Activos
            </h6>

            {wazeLinks.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <FontAwesomeIcon icon={faRoute} size="2x" className="mb-3" style={{ opacity: 0.3 }} />
                <p>No hay links de Waze activos</p>
              </div>
            ) : (
              <ListGroup>
                {wazeLinks.map((link) => (
                  <ListGroup.Item key={link.id}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div style={{ flex: 1 }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <strong>{link.contactName}</strong>
                          {link.isActive && (
                            <Badge bg="success" pill>Activo</Badge>
                          )}
                        </div>
                        <small className="text-muted d-block">
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          {formatTimeAgo(link.createdAt)}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleStopTracking(link.contactId, link.contactName)}
                          disabled={stoppingTracking === link.contactId}
                        >
                          {stoppingTracking === link.contactId ? (
                            <Spinner size="sm" />
                          ) : (
                            <FontAwesomeIcon icon={faStop} />
                          )}
                        </Button>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Col>

          {/* Columna 2: Ubicaciones Activas */}
          <Col md={6}>
            <h6 style={{ fontWeight: 600, marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
              Ubicaciones Activas
            </h6>

            {loading && activeLocations.length === 0 ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : activeLocations.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" className="mb-3" style={{ opacity: 0.3 }} />
                <p>No hay ubicaciones activas</p>
              </div>
            ) : (
              <ListGroup>
                {activeLocations.map((loc, index) => (
                  <ListGroup.Item key={index}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div style={{ flex: 1 }}>
                        <strong>{loc.contactName}</strong>
                        <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
                          {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                        </div>
                        <small className="text-muted">
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          {formatTimeAgo(loc.timestamp)}
                          {loc.speed && ` • ${Math.round(loc.speed)} km/h`}
                          {loc.accuracy && ` • ±${Math.round(loc.accuracy)}m`}
                        </small>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        href={getGoogleMapsUrl(loc.latitude, loc.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver Mapa
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Col>
        </Row>

        {/* Info adicional */}
        {activeLocations.length > 0 && (
          <div className="mt-3 p-3" style={{ 
            backgroundColor: 'var(--bs-light)', 
            borderRadius: '8px',
            fontSize: '0.85rem',
            color: 'var(--bs-secondary)'
          }}>
            <FontAwesomeIcon icon={faClock} className="me-2" />
            Las ubicaciones se actualizan automáticamente cada 30 segundos
          </div>
        )}

      </Card.Body>
    </Card>
  );
};

export default LocationsSection;